import React, { lazy, Suspense, useEffect } from 'react';
import './App.css';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '~/config/queryClient';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { store } from '~/initializeStore';
import AuthChecker from './components/AuthChecker/AuthChecker';
import Auth from './pages/Auth/Auth';
import ProtectedRoute from '~/components/ProtectedRoute/ProtectedRoute';
import { useTranslation } from 'react-i18next';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
const Dashboard = lazy(() => import('~/pages/Dashboard/Dashboard'));
// Galeria komponentów tylko w trybie dev: `import.meta.env.DEV` jest stałą w buildzie, więc
// bundler wycina ten import i galeria nie trafia do produkcji.
const DevUiGallery = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiGallery')) : null;
const DevUiShell = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiShell')) : null;
const DevUiSetup = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiSetup')) : null;
const DevUiProductGroups = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiProductGroups'))
  : null;
const DevUiBatches = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiBatches')) : null;
const DevUiPoSList = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiPoSList')) : null;
const DevUiPoSSettings = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiPoSSettings'))
  : null;
const DevUiIssuePoS = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiIssuePoS'))
  : null;
const DevUiPeriod = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiPeriod')) : null;
const DevUiClosePeriod = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiClosePeriod'))
  : null;
const DevUiRegisterBatch = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiRegisterBatch'))
  : null;
const DevUiSale = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiSale')) : null;
const DevUiBatchDetails = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiBatchDetails'))
  : null;
const DevUiReports = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiReports')) : null;
const DevUiDrive = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiDrive')) : null;
const DevUiDeadlines = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiDeadlines'))
  : null;
const DevUiHome = import.meta.env.DEV ? lazy(() => import('~/pages/DevUi/DevUiHome')) : null;
const DevUiSuppliers = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiSuppliers'))
  : null;
const DevUiSupplierDetails = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiSupplierDetails'))
  : null;
const DevUiSupplierViolation = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiSupplierViolation'))
  : null;
const DevUiPoSDetails = import.meta.env.DEV
  ? lazy(() => import('~/pages/DevUi/DevUiPoSDetails'))
  : null;

const AppRoutes = () => (
  <AuthChecker>
    <Switch>
      <Route path="/auth" component={Auth} />
      <ProtectedRoute path="/" component={Dashboard} />
    </Switch>
  </AuthChecker>
);

const Routes = () => {
  return (
    <>
      <Suspense fallback={<FullPageLoader />}>
        {/* Poza AuthChecker i ProtectedRoute: galeria nie wymaga logowania ani Firebase. */}
        <Switch>
          {DevUiShell && <Route path="/dev/ui/shell" component={DevUiShell} />}
          {DevUiSetup && <Route path="/dev/ui/setup" component={DevUiSetup} />}
          {DevUiProductGroups && (
            <Route path="/dev/ui/product-groups" component={DevUiProductGroups} />
          )}
          {DevUiBatches && <Route path="/dev/ui/batches" component={DevUiBatches} />}
          {DevUiPoSList && <Route path="/dev/ui/pos-list" component={DevUiPoSList} />}
          {DevUiPoSSettings && <Route path="/dev/ui/pos-settings" component={DevUiPoSSettings} />}
          {DevUiIssuePoS && <Route path="/dev/ui/issue-pos" component={DevUiIssuePoS} />}
          {DevUiPeriod && <Route path="/dev/ui/period" component={DevUiPeriod} />}
          {DevUiClosePeriod && <Route path="/dev/ui/close-period" component={DevUiClosePeriod} />}
          {DevUiRegisterBatch && (
            <Route path="/dev/ui/register-batch" component={DevUiRegisterBatch} />
          )}
          {DevUiSale && <Route path="/dev/ui/sale" component={DevUiSale} />}
          {DevUiBatchDetails && (
            <Route path="/dev/ui/batch-details" component={DevUiBatchDetails} />
          )}
          {DevUiPoSDetails && <Route path="/dev/ui/pos-details" component={DevUiPoSDetails} />}
          {DevUiReports && <Route path="/dev/ui/reports" component={DevUiReports} />}
          {DevUiHome && <Route path="/dev/ui/home" component={DevUiHome} />}
          {DevUiDrive && <Route path="/dev/ui/drive" component={DevUiDrive} />}
          {DevUiDeadlines && <Route path="/dev/ui/deadlines" component={DevUiDeadlines} />}
          {DevUiSuppliers && <Route path="/dev/ui/suppliers" component={DevUiSuppliers} />}
          {DevUiSupplierDetails && (
            <Route path="/dev/ui/supplier-details" component={DevUiSupplierDetails} />
          )}
          {DevUiSupplierViolation && (
            <Route path="/dev/ui/supplier-violation" component={DevUiSupplierViolation} />
          )}
          {DevUiGallery && <Route path="/dev/ui" component={DevUiGallery} />}
          <Route path="/" component={AppRoutes} />
        </Switch>
      </Suspense>
    </>
  );
};

function App() {
  const { i18n } = useTranslation('common');

  useEffect(() => {
    if (['pl', 'en'].includes(i18n.language) || !i18n.language) {
      return;
    }
    const [lang] = i18n.language.split('-');
    i18n.changeLanguage(lang || 'en');
  }, [i18n]);

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Router>
          <Routes />
        </Router>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
